import { useEffect, useState } from "react";
import { callLlm } from "../lib/forecast/llmClient.js";
import {
  cleanConclusion,
  cleanEventPhrase,
  conclusionPrompt,
  likelihoodPhrase,
  yesNoEventPrompt,
  yesNoHeadline,
} from "../lib/report/brief.js";

const CACHE_PREFIX = "sooth-briefConclusion:";
// Part of every key: bump it when the prompt or the cleaner changes, so old answers are redone.
// v2: a yes/no headline states the larger side's chance on the question's own event ("Sayer puts
// an 82% chance against …"); the model writes only the event, so a cached yes/no answer is the
// event phrase.
const PROMPT_VERSION = 2;
// Requests in flight, by cache key: reopening the brief before a sentence arrives joins the
// request already made rather than paying for a second.
const inflight = new Map();

// Stored for a headline the model wrote unusably, so the brief shows its own at once.
const NONE = "∅";
// Asked this session and failed (the model could not be reached): settled until a reload.
const failed = new Set();

/** `undefined` when `key` has not been asked, `null` when no usable headline came back. */
function lookup(key) {
  if (failed.has(key)) return null;
  try {
    const raw = globalThis.localStorage?.getItem(CACHE_PREFIX + key);
    if (raw == null || raw === "") return undefined;
    return raw === NONE ? null : raw;
  } catch {
    return undefined;
  }
}

// Every reworded question, leader or likelihood phrase is a new key, so the cache is capped and
// the oldest-written entries go first. The index lists cached keys oldest first.
export const CACHE_MAX_ENTRIES = 200;
const INDEX_KEY = "sooth-briefConclusionIndex";

function readIndex(ls) {
  try {
    const raw = JSON.parse(ls.getItem(INDEX_KEY) || "null");
    return Array.isArray(raw) ? raw.filter((k) => typeof k === "string") : null;
  } catch {
    return null;
  }
}

/** Cache `value` under `key`, evicting the oldest entries beyond CACHE_MAX_ENTRIES. */
export function writeCache(key, value) {
  try {
    const ls = globalThis.localStorage;
    if (!ls) return;
    let index = readIndex(ls);
    if (!index) {
      // Entries from before the index existed have no write order: drop them once.
      const legacy = [];
      for (let i = 0; i < ls.length; i++) {
        const k = ls.key(i);
        if (k && k.startsWith(CACHE_PREFIX)) legacy.push(k);
      }
      legacy.forEach((k) => ls.removeItem(k));
      index = [];
    }
    index = index.filter((k) => k !== key);
    index.push(key);
    while (index.length > CACHE_MAX_ENTRIES) ls.removeItem(CACHE_PREFIX + index.shift());
    ls.setItem(INDEX_KEY, JSON.stringify(index));
    ls.setItem(CACHE_PREFIX + key, value);
  } catch {
    // Storage full or blocked: the headline is simply asked for again next time.
  }
}

/**
 * The brief's headline as a sentence stating the forecast's conclusion, written once per
 * report by the small LLM (no web search) and cached in this browser. Null until it arrives,
 * when it fails, or while `enabled` is false — callers fall back to their own headline.
 *
 * @param {{ enabled: boolean, question: string, multi: boolean,
 *   lead: {label: string, p: number}|null, runnerUp: {label: string}|null,
 *   probability: number|null, unit?: string, criteria?: string }} deps  unit and criteria
 *   say what a numeric outcome measures
 * @returns {{ text: string|null, pending: boolean }} `pending` while the sentence is being
 *   written, so the caller can hold the space rather than flash a fallback.
 */
export function useBriefConclusion({ enabled, ...deps }) {
  const key = enabled ? conclusionKeyOf(deps) : null;
  const known = key ? lookup(key) : undefined;
  const [fetched, setFetched] = useState({});
  useEffect(() => {
    if (!key || known !== undefined || key in fetched) return undefined;
    let live = true;
    requestConclusion(key, deps).then((sentence) => {
      if (live) setFetched((f) => ({ ...f, [key]: sentence }));
    });
    return () => {
      live = false;
    };
    // `fetched` is read only to skip a key already asked for; re-running on it would re-ask.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, known !== undefined]);
  const answer = known ?? (key ? fetched[key] : null) ?? null;
  // A yes/no answer is the event; the sentence and its number are the app's own.
  const text = answer && !deps.multi ? yesNoHeadline(deps.probability, answer) : answer;
  return { text, pending: !!key && known === undefined && !(key in fetched) };
}

/**
 * The cache key for a headline: everything the prompt is given — the question, the leader, the
 * runner-up, the calibrated wording and what a range measures — so a reforecast changing any
 * of them gets its own. Null when there is nothing to write about.
 */
function conclusionKeyOf({ question, multi, lead, runnerUp, unit = "", criteria = "" }) {
  if (!question || !lead) return null;
  // A yes/no event phrase holds at any probability, so a reforecast reuses it.
  const phrase = multi ? likelihoodPhrase({ multi, p: lead.p }) : "";
  return JSON.stringify([
    PROMPT_VERSION,
    question,
    multi ? lead.label : "",
    multi ? runnerUp?.label || "" : "",
    phrase,
    multi ? unit : "",
    criteria,
  ]);
}

/** The headline for `key`, asked for once: a second caller joins the request in flight. */
function requestConclusion(key, deps) {
  const { question, multi, lead, runnerUp, probability, unit = "", criteria = "" } = deps;
  if (!inflight.has(key)) {
    const phrase = likelihoodPhrase({ multi, p: multi ? lead.p : probability });
    const prompt = multi
      ? conclusionPrompt({ question, multi, lead, runnerUp, probability, unit, criteria })
      : yesNoEventPrompt({ question, criteria });
    inflight.set(
      key,
      callLlm(prompt, { webSearch: false })
        .then(
          (text) => {
            const sentence = multi
              ? cleanConclusion(text, question, lead.label, phrase)
              : cleanEventPhrase(text, question);
            writeCache(key, sentence || NONE);
            return sentence || null;
          },
          () => {
            failed.add(key);
            return null;
          },
        )
        .finally(() => inflight.delete(key)),
    );
  }
  return inflight.get(key);
}

/**
 * Starts writing a report's headline before the report is opened, so it is cached (or on its
 * way) by the time the brief asks. Does nothing when it is already cached or there is no lead.
 * @param {{ question: string, multi: boolean, lead: {label: string, p: number}|null,
 *   runnerUp: {label: string}|null, probability: number|null, unit?: string, criteria?: string }} deps
 */
export function prefetchBriefConclusion(deps) {
  const key = conclusionKeyOf(deps);
  if (key && lookup(key) === undefined) requestConclusion(key, deps);
}
